import React from "react";
import { inject, observer } from "mobx-react";
import { IMaskInput } from "react-imask";
import { RDCSlot } from "./RDCSlot";
import { ErrorMessage } from "../ErrorMessage";
import { useEffect, useMemo, useRef, useState } from "react";
import { LuCircleAlert, LuTriangleAlert } from "react-icons/lu";
import axios from "axios";

// CheckProcessing can succeed with non-blocking warnings (e.g. a scanned
// amount that doesn't match the entered amount) in `messages` rather than
// `errors`. Keeps only non-empty strings, de-duplicated so each renders once
// under a unique key.
function toProcessingWarnings(messages) {
  if (!Array.isArray(messages)) return [];
  const warnings = messages.filter(
    (message) => typeof message === "string" && message.length > 0,
  );
  return [...new Set(warnings)];
}

// Shared layout for the error and warning lists under the image slots; each
// list only sets its own color.
const MESSAGE_LIST_STYLE = {
  listStyleType: "none",
  padding: 0,
  margin: 0,
  fontSize: "12px",
  display: "flex",
  flexDirection: "column",
  gap: "4px",
  paddingInline: "10px",
};

const PaymentMethodRDC = (props) => {
  const [loading, setLoading] = useState(false);
  const inputs = props.vTerminal.rdcInputs;
  const rows = props.vTerminal.rdcRows || [];
  const [errors, setErrors] = useState([]);
  const [warnings, setWarnings] = useState([]);
  // Id of the latest CheckProcessing request. A response that isn't for the
  // latest scan (the amount or an image changed while it was in flight) is
  // ignored, so it can't overwrite the newer scan's results.
  const latestScanIdRef = useRef(0);
  // Hosts can opt out with `rdc: { showWarnings: false }`. The scan is still
  // accepted and the ready payload still includes `messages`, so the host can
  // handle the warnings itself.
  const showWarnings = props.config?.rdc?.showWarnings !== false;

  const amount = useMemo(() => {
    return props.vTerminal.paymentPage?.paymentMethods?.rdcAmount || 0;
  }, [props.vTerminal.paymentPage?.paymentMethods?.rdcAmount]);

  // Disable amount input when both images are checked and no errors
  const isAmountDisabled = useMemo(() => {
    const frontImage =
      props.vTerminal.paymentPage?.paymentMethods?.rdcFrontImage;
    const backImage = props.vTerminal.paymentPage?.paymentMethods?.rdcBackImage;

    return (
      frontImage?.checked === true &&
      frontImage?.error === false &&
      backImage?.checked === true &&
      backImage?.error === false
    );
  }, [
    props.vTerminal.paymentPage?.paymentMethods?.rdcFrontImage,
    props.vTerminal.paymentPage?.paymentMethods?.rdcBackImage,
  ]);

  const isAmountOverMax = useMemo(() => {
    const val = parseFloat(amount);
    return !isNaN(val) && val > 25000;
  }, [amount]);

  const showRDCCapture =
    !!props.vTerminal.paymentPage?.paymentMethods?.rdcAccountHolderName &&
    !!props.vTerminal.paymentPage?.paymentMethods?.rdcAmount &&
    !isAmountOverMax;
  useEffect(() => {
    props?.handleResize?.();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    showRDCCapture,
    props.vTerminal.getpaymentPageErrorsMessages,
    errors,
    warnings,
  ]);

  const checkImageFetch = async (payload, signal) => {
    const scanId = ++latestScanIdRef.current;
    const isStale = () =>
      signal?.aborted === true || scanId !== latestScanIdRef.current;
    const images = ["rdcFrontImage", "rdcBackImage"];

    // Helper function to update image status
    const updateImageStatus = (hasError) => {
      images.forEach((image) => {
        const currentValue =
          props.vTerminal.paymentPage?.paymentMethods?.[image]?.value;

        props.vTerminal.handleTextChangeACH(image, {
          value: currentValue,
          checked: true,
          error: hasError,
        });
        props.vTerminal.setPaymentPageError(
          `paymentMethods${image}Error`,
          false,
        );
        props.vTerminal.setPaymentPageErrorMessage(image, null);
      });
    };

    // Helper function to extract error messages
    const extractErrorMessages = (data) => {
      if (data?.responseText) {
        return [data.responseText];
      } else if (data?.errors && Array.isArray(data.errors)) {
        return data.errors;
      } else if (data?.errors) {
        return [data.errors];
      } else if (data?.message) {
        return [data.message];
      }
      return [
        "An error occurred while processing the check. Please try again.",
      ];
    };

    try {
      setLoading(true);
      const isValid =
        payload &&
        typeof payload.entryPoint === "string" &&
        typeof payload.frontImage === "string" &&
        typeof payload.rearImage === "string" &&
        typeof payload.checkAmount === "number";

      if (!isValid) {
        console.error("Invalid data structure");
        setLoading(false);
        return;
      }

      const { data } = await axios.post(
        `${process.env.REACT_APP_URL_API}CheckCapture/CheckProcessing`,
        payload,
        {
          headers: {
            "Content-Type": "application/json",
            requestToken: props.vTerminal.token,
          },
          signal,
        },
      );

      if (isStale()) return;

      // Set the token
      const token = data?.pageIdentifier;
      token && props.vTerminal.setToken(token);

      const hasErrors = data?.errors?.length > 0;
      const isSuccess = data?.isSuccess === true || data.success === true;

      // Update image status
      updateImageStatus(hasErrors || !isSuccess);

      // Prepare common data for postMessage
      const { pageIdentifier, ...dataWithoutPageIdentifier } = data || {};
      const messageData = {
        ...dataWithoutPageIdentifier,
        inputHolderName:
          props.vTerminal.paymentPage?.paymentMethods?.rdcAccountHolderName,
        inputAmount:
          props.vTerminal.paymentPage?.paymentMethods?.rdcAmount,
      };

      // If no errors and isSuccess is true, update the check details from OCR
      if (!hasErrors && isSuccess) {
        setErrors([]);
        // Warnings are non-blocking: the scan was accepted, so they're shown
        // alongside the accepted images, never instead of them.
        const warnings = toProcessingWarnings(data?.messages);
        setWarnings(warnings);
        const checkDetails = [
          { field: "rdcCaptureID", value: data?.id },
          { field: "rdcAccountNumber", value: data?.ocrAccountNumber },
          { field: "rdcRoutingNumber", value: data?.ocrRoutingNumber },
        ];

        checkDetails.forEach(({ field, value }) => {
          props.vTerminal.handleTextChangeACH(field, value);
        });

        window.parent.postMessage(
          {
            event: "callback-payabli-function-ready" + props.componentId,
            // `warnings` is sent regardless of `rdc.showWarnings`, so a host
            // that hides the in-component list can still show it itself.
            data: { ...messageData, warnings },
          },
          "*",
        );
      } else {
        const errorMessages = extractErrorMessages(data);
        setErrors(errorMessages);
        setWarnings([]);
        window.parent.postMessage(
          {
            event: "callback-payabli-function-error" + props.componentId,
            data: messageData,
          },
          "*",
        );
      }
    } catch (error) {
      // Superseded or aborted (including on unmount): not an error to show.
      if (isStale()) return;

      console.error(`Error checking checks:`, error);

      // Set the token even on error (400 Bad Request still returns pageIdentifier)
      const token = error.response?.data?.pageIdentifier;
      token && props.vTerminal.setToken(token);

      updateImageStatus(true);
      
      const errorMessages = extractErrorMessages(
        error.response?.data || { message: error.message },
      );
      setErrors(errorMessages);
      setWarnings([]);

      window.parent.postMessage(
        {
          event: "callback-payabli-function-error" + props.componentId,
          data: {
            success: false,
            errors: errorMessages,
            inputHolderName:
              props.vTerminal.paymentPage?.paymentMethods?.rdcAccountHolderName,
            inputAmount:
              props.vTerminal.paymentPage?.paymentMethods?.rdcAmount,
          }
        },
        "*",
      );
    } finally {
      if (!isStale()) setLoading(false);
    }
  };

  useEffect(() => {
    setErrors([]);
    setWarnings([]);
    // A pending scan for the previous images/amount is now stale; a new scan
    // below sets loading again.
    setLoading(false);
    if (
      props.vTerminal.paymentPage?.paymentMethods?.rdcFrontImage?.value &&
      !props.vTerminal.paymentPage?.paymentMethods?.rdcFrontImage?.checked &&
      props.vTerminal.paymentPage?.paymentMethods?.rdcBackImage?.value &&
      !props.vTerminal.paymentPage?.paymentMethods?.rdcBackImage?.checked
    ) {
      const payload = {
        frontImage:
          props.vTerminal.paymentPage?.paymentMethods?.rdcFrontImage?.value?.split(
            ",",
          )[1],
        rearImage:
          props.vTerminal.paymentPage?.paymentMethods?.rdcBackImage?.value?.split(
            ",",
          )[1],
        entryPoint: props.vTerminal?.entryPoint,
        checkAmount: Number((amount * 100).toFixed(0)),
      };
      const controller = new AbortController();
      checkImageFetch(payload, controller.signal);
      // Cancel this scan when the images or amount change, or on unmount.
      return () => controller.abort();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    props.vTerminal.paymentPage?.paymentMethods?.rdcFrontImage?.value,
    props.vTerminal.paymentPage?.paymentMethods?.rdcBackImage?.value,
    amount,
  ]);

  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        gap: "20px",
        paddingBlock: "2px",
      }}
    >
      {/* Iterate over the rows */}
      {rows.map((row, idx) => (
        <div
          key={`rdc-${idx}`}
          style={{ alignItems: "start" }}
          className="row"
          id={`rdc-${idx}`}
        >
          {/* Iterate over the items in the row */}
          {row.map((item, itemIdx) => {
            // RDC Account Holder Name
            if (item === "rdcAccountHolderName") {
              const data = inputs[item];
              return (
                <div key={itemIdx} className={`fullw-440 col-${data?.size} `}>
                  {props.showPopoverError === true && (
                    <ErrorMessage
                      showPopoverError={true}
                      value={
                        props.vTerminal.getpaymentPageErrorsMessages
                          .rdcAccountHolderName
                      }
                    />
                  )}
                  <div
                    key={itemIdx}
                    id="rdcAccountHolderName"
                    className={data?.floating ? "form-floating" : ""}
                  >
                    {/* Display the label */}
                    {!data?.floating && (
                      <label htmlFor="rdcAccountHolderName">
                        {data?.label}
                      </label>
                    )}
                    {/* Display the input */}
                    <IMaskInput
                      id="rdcAccountHolderName"
                      name="rdcAccountHolderName"
                      placeholder={data?.placeholder}
                      mask={props.global.maskValidator(
                        "alphanumericspaceslatin",
                      )}
                      value={
                        props.vTerminal.paymentPage?.paymentMethods
                          ?.rdcAccountHolderName || ""
                      }
                      unmask={true}
                      onAccept={(value) => {
                        props.vTerminal.handleTextChangeACH(
                          "rdcAccountHolderName",
                          value,
                        );
                        props.vTerminal.setPaymentPageError(
                          "paymentMethodsRdcAccountHolderNameError",
                          false,
                        );
                        props.vTerminal.setPaymentPageErrorMessage(
                          "rdcAccountHolderName",
                          null,
                        );
                      }}
                      className={
                        props.vTerminal.getPaymentPageErrors
                          .paymentMethodsRdcAccountHolderNameError
                          ? "form-control input-error"
                          : "form-control"
                      }
                    />
                    {data?.floating === true && (
                      <label htmlFor="rdcAccountHolderName">
                        {data?.label}
                      </label>
                    )}
                    {/* Display the error message */}
                    {props.showPopoverError === false && (
                      <ErrorMessage
                        showPopoverError={false}
                        value={
                          props.vTerminal.getpaymentPageErrorsMessages
                            .rdcAccountHolderName
                        }
                      />
                    )}
                  </div>
                </div>
              );
            }
            // RDC Amount
            if (item === "rdcAmount") {
              const data = inputs[item];
              return (
                <div key={itemIdx} className={`fullw-440 col-${data?.size} `}>
                  {(props.showPopoverError === true || isAmountOverMax) && (
                    <ErrorMessage
                      showPopoverError={true}
                      value={
                        props.vTerminal.getpaymentPageErrorsMessages
                          .rdcAmount
                      }
                    />
                  )}
                  <div
                    key={itemIdx}
                    id="rdcAmount"
                    className={data?.floating ? "form-floating" : ""}
                  >
                    {/* Display the label */}
                    {data?.label && !data?.floating && (
                      <label htmlFor="rdcAmount">{data?.label}</label>
                    )}
                    {/* Display the input */}
                    <IMaskInput
                      id="rdcAmount"
                      name="rdcAmount"
                      placeholder={data?.placeholder}
                      inputMode="decimal"
                      mask="$num"
                      blocks={{
                        num: {
                          mask: Number,
                          thousandsSeparator: ",",
                          scale: 2,
                          signed: false,
                          radix: ".",
                          padFractionalZeros: true,
                        },
                      }}
                      value={
                        props.vTerminal.paymentPage?.paymentMethods
                          ?.rdcAmount || ""
                      }
                      unmask={true}
                      disabled={isAmountDisabled}
                      onAccept={(value) => {
                        props.vTerminal.handleTextChangeACH("rdcAmount", value);
                        const numVal = parseFloat(value);
                        if (!isNaN(numVal) && numVal > 25000) {
                          props.vTerminal.setPaymentPageError(
                            "paymentMethodsRdcAmountError",
                            true,
                          );
                          props.vTerminal.setPaymentPageErrorMessage(
                            "rdcAmount",
                            "Amount cannot exceed $25,000",
                          );
                        } else {
                          props.vTerminal.setPaymentPageError(
                            "paymentMethodsRdcAmountError",
                            false,
                          );
                          props.vTerminal.setPaymentPageErrorMessage(
                            "rdcAmount",
                            null,
                          );
                        }
                      }}
                      className={
                        props.vTerminal.getPaymentPageErrors
                          .paymentMethodsRdcAmountError
                          ? "form-control input-error"
                          : "form-control"
                      }
                    />
                    {data?.floating === true && (
                      <label htmlFor="rdcAmount">{data?.label}</label>
                    )}
                    {/* Display the error message */}
                    {props.showPopoverError === false && (
                      <ErrorMessage
                        showPopoverError={false}
                        value={
                          props.vTerminal.getpaymentPageErrorsMessages
                            .rdcAmount
                        }
                      />
                    )}
                  </div>
                </div>
              );
            }
            // RDC Front Image
            if (item === "rdcFrontImage") {
              const data = inputs[item];
              return (
                <div key={itemIdx} className={`fullw-440 col-${data?.size} `}>
                  <RDCSlot
                    disabled={!showRDCCapture || isAmountDisabled}
                    componentId={props.componentId}
                    error={
                      props.vTerminal.getPaymentPageErrors
                        .paymentMethodsRdcFrontImageError
                    }
                    checked={
                      props.vTerminal?.paymentPage?.paymentMethods
                        ?.rdcFrontImage?.checked
                    }
                    checkedError={
                      props.vTerminal?.paymentPage?.paymentMethods
                        ?.rdcFrontImage?.error
                    }
                    value={
                      props.vTerminal?.paymentPage?.paymentMethods
                        ?.rdcFrontImage?.value || ""
                    }
                    onChange={(value) => {
                      props.vTerminal.handleTextChangeACH(item, {
                        value: value,
                        checked: false,
                        error: null,
                      });
                      props.vTerminal.setPaymentPageError(
                        "paymentMethodsRdcFrontImageError",
                        false,
                      );
                      props.vTerminal.setPaymentPageErrorMessage(
                        "rdcFrontImage",
                        null,
                      );
                    }}
                    side="front"
                    loading={loading}
                  />
                </div>
              );
            }
            if (item === "rdcBackImage") {
              const data = inputs[item];
              return (
                <div key={itemIdx} className={`fullw-440 col-${data?.size} `}>
                  <RDCSlot
                    disabled={!showRDCCapture || isAmountDisabled}
                    componentId={props.componentId}
                    error={
                      props.vTerminal.getPaymentPageErrors
                        .paymentMethodsRdcBackImageError
                    }
                    value={
                      props.vTerminal?.paymentPage?.paymentMethods?.rdcBackImage
                        ?.value || ""
                    }
                    checked={
                      props.vTerminal?.paymentPage?.paymentMethods?.rdcBackImage
                        ?.checked
                    }
                    checkedError={
                      props.vTerminal?.paymentPage?.paymentMethods?.rdcBackImage
                        ?.error
                    }
                    onChange={(value) => {
                      props.vTerminal.handleTextChangeACH(item, {
                        value: value,
                        checked: false,
                        error: null,
                      });
                      props.vTerminal.setPaymentPageError(
                        "paymentMethodsRdcBackImageError",
                        false,
                      );
                      props.vTerminal.setPaymentPageErrorMessage(
                        "rdcBackImage",
                        null,
                      );
                    }}
                    loading={loading}
                    side="back"
                  />
                </div>
              );
            }
            return null;
          })}
        </div>
      ))}
      {errors.length > 0 && (
        <ul style={{ ...MESSAGE_LIST_STYLE, color: "rgb(127, 29, 29)" }}>
          {errors.map((error, idx) => (
            <li
              key={idx}
              style={{
                display: "flex",
                alignItems: "center",
                gap: "4px",
              }}
            >
              <LuCircleAlert
                style={{
                  aspectRatio: "1/1",
                  width: "25px",
                }}
                size={12}
              />
              {error}
            </li>
          ))}
        </ul>
      )}
      {/* Same tone as the Payabli app's RDC warning (text-warning-900). */}
      {showWarnings && errors.length === 0 && warnings.length > 0 && (
        <ul style={{ ...MESSAGE_LIST_STYLE, color: "hsl(22 78% 26%)" }}>
          {warnings.map((warning) => (
            <li
              key={warning}
              style={{
                display: "flex",
                alignItems: "flex-start",
                gap: "4px",
              }}
            >
              <LuTriangleAlert
                role="img"
                aria-label="Warning"
                style={{
                  aspectRatio: "1/1",
                  width: "25px",
                  flexShrink: 0,
                }}
                size={12}
              />
              {warning}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

export default inject("vTerminal", "global")(observer(PaymentMethodRDC));
